import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './styles.css';
import { useTranslation } from 'react-i18next';

const LightningIcon = () => (
	<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8.5 1L3 9.5h4.5L6.5 15 13 6.5H8.5L9.5 1z"
			stroke="currentColor"
			strokeWidth="1.2"
			strokeLinejoin="round"
		/>
	</svg>
);

const ChevronIcon = () => (
	<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const CheckIcon = () => (
	<svg className={styles.checkmark} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M2 5L4.5 7.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export default function QuickSelectDropdown({
	options,
	initialCheckedKeys,
	onConfirm,
	triggerLabel,
	masterKey,
	hideLabelOnMobile = false,
	align = 'left',
}) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [checkedKeys, setCheckedKeys] = useState(new Set());
	const wrapperRef = useRef(null);

	const label = triggerLabel || t('Quick selection');

	const handleOpen = useCallback(() => {
		setCheckedKeys(new Set(initialCheckedKeys || []));
		setIsOpen(true);
	}, [initialCheckedKeys]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleToggle = useCallback(() => {
		if (isOpen) {
			handleClose();
		} else {
			handleOpen();
		}
	}, [isOpen, handleOpen, handleClose]);

	const handleCheckboxToggle = useCallback(
		(key) => {
			setCheckedKeys((prev) => {
				const next = new Set(prev);
				const willCheck = !next.has(key);
				if (willCheck) {
					next.add(key);
				} else {
					next.delete(key);
				}

				if (masterKey) {
					if (key === masterKey) {
						// Master cascades to every option.
						options.forEach((opt) => (willCheck ? next.add(opt.key) : next.delete(opt.key)));
					} else {
						// Master must match: checked iff every non-master option is checked.
						const allOthersChecked = options.every((opt) => opt.key === masterKey || next.has(opt.key));
						if (allOthersChecked) {
							next.add(masterKey);
						} else {
							next.delete(masterKey);
						}
					}
				}

				return next;
			});
		},
		[options, masterKey],
	);

	const handleConfirm = useCallback(() => {
		const initial = new Set(initialCheckedKeys || []);
		const uncheckedKeys = new Set();
		initial.forEach((key) => {
			if (!checkedKeys.has(key)) {
				uncheckedKeys.add(key);
			}
		});
		onConfirm(checkedKeys, uncheckedKeys);
		setIsOpen(false);
	}, [checkedKeys, initialCheckedKeys, onConfirm]);

	useEffect(() => {
		if (!isOpen) return;
		const handleClickOutside = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				handleClose();
			}
		};
		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				handleClose();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isOpen, handleClose]);

	return (
		<div className={`${styles.triggerWrapper} ${hideLabelOnMobile ? styles.hideLabelOnMobile : ''}`} ref={wrapperRef}>
			<button type="button" className={styles.trigger} onClick={handleToggle} aria-expanded={isOpen}>
				<span className={styles.triggerIcon}>
					<LightningIcon />
				</span>
				<span className={styles.triggerLabel}>{label}</span>
				<span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>
					<ChevronIcon />
				</span>
			</button>
			{isOpen && (
				<div className={`${styles.dropdown} ${align === 'right' ? styles.dropdownAlignRight : ''}`} role="group">
					{options.map((option) => {
						const isChecked = checkedKeys.has(option.key);
						return (
							<div
								key={option.key}
								className={styles.option}
								onClick={() => handleCheckboxToggle(option.key)}
								role="checkbox"
								aria-checked={isChecked}
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === ' ' || e.key === 'Enter') {
										e.preventDefault();
										handleCheckboxToggle(option.key);
									}
								}}
							>
								<span className={`${styles.checkbox} ${isChecked ? styles.checkboxChecked : ''}`}>
									{isChecked && <CheckIcon />}
								</span>
								<span className={isChecked ? styles.optionLabelChecked : styles.optionLabel}>{option.label}</span>
								{typeof option.count === 'number' ? <span className={styles.optionCount}>{option.count}</span> : null}
							</div>
						);
					})}
					<div className={styles.actions}>
						<button type="button" className={styles.cancelBtn} onClick={handleClose}>
							{t('Cancel')}
						</button>
						<button
							type="button"
							className={`${styles.confirmBtn} ${checkedKeys.size === 0 ? styles.confirmBtnDisabled : ''}`}
							onClick={handleConfirm}
						>
							{t('Confirm')}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
